import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemButton,
  ListItemText,
  Portal,
  Slide,
  SlideProps,
  Snackbar,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ClientConfig } from '@/types/types';
import { useAppSelector } from '@/app/hooks';
import { ORG_MANHEIM_CODE } from '@/constants';
import { CloseIcon } from './Icons';
import { ViewportList } from 'react-viewport-list';

interface MissingItemsDialogProps {
  open: boolean;
  missingItems: string[];
  setOpen: (open: boolean) => void;
}

export default function MissingItemsDialog({
  open,
  missingItems,
  setOpen,
}: MissingItemsDialogProps) {
  const [t] = useTranslation();
  const isDesktop = useMediaQuery('(min-width:900px)');
  const theme = useTheme<Theme>();
  const siteMetadata: ClientConfig = useAppSelector(
    (state: any) => state.user.siteMetadata,
  );
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const handleSnackBarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') return;
    setSnackBarOpen(false);
  };
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(missingItems.join(', ').toUpperCase());
    setSnackBarOpen(true);
  };
  const SlideLeftTransition = (props: SlideProps) => (
    <Slide {...props} direction="left" />
  );

  return (
    <>
      <Dialog
        fullScreen={!isDesktop} // Makes the dialog fullscreen on mobile
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          style: {
            borderRadius: isDesktop ? '10px' : '0px', // Remove border radius on mobile
            backgroundColor: theme.palette.background.default,
            minHeight: isDesktop ? '70px' : '100vh', // Full height on mobile
            minWidth: isDesktop ? '600px' : '100vw', // Full width on mobile
            maxWidth: isDesktop ? '900px' : '100vw',
            padding: '20px',
            border: isDesktop ? '1px solid' : 'none', // Remove border on mobile
            borderColor: theme.palette.divider,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <DialogTitle
            sx={{
              fontSize: '16px',
              fontWeight: '600',
              paddingLeft: isDesktop ? 0 : 1,
            }}
            data-testid="missing-items-title"
          >
            {t('missingItemsDialogs.missing')}
          </DialogTitle>
          <Button
            onClick={() => setOpen(false)}
            sx={(theme) => ({
              color: theme.palette.text.primary,
              width: '14px',
              height: '14px',
              padding: isDesktop ? 0 : 5,
              minWidth: '14px',
              justifyContent: isDesktop ? 'flex-end' : 'flex-start',
            })}
            data-testid="send-message-close-icon"
          >
            <CloseIcon
              style={{ width: '14px', height: '14px' }}
              viewBox="0 0 14 14"
            />
          </Button>
        </Box>
        <DialogContent
          sx={(theme) => ({
            padding: '0px 0px',
            border: isDesktop ? '1px solid' : 'none', // Remove border on mobile
            borderColor: theme.palette.divider,
            borderRadius: isDesktop ? '10px' : 0,
          })}
        >
          <Typography
            variant="subtitle1"
            sx={{
              paddingLeft: '3%',
              fontWeight: 'bold',
              backgroundColor: theme.palette.background.default,
            }}
          >
            {siteMetadata?.orgCode === ORG_MANHEIM_CODE
              ? t('missingItemsDialogs.subtitle')
              : t('missingItemsDialogs.subtitle2')}
          </Typography>
          <Box
            ref={listRef}
            sx={{
              height: isDesktop ? 200 : 'auto',
              overflowY: 'auto',
              width: '100%',
            }}
          >
            <ViewportList viewportRef={listRef} items={missingItems}>
              {(item, index) => (
                <ListItem
                  key={index}
                  disablePadding
                  sx={(theme) => ({
                    padding: isDesktop ? 0 : '12px 16px',
                    backgroundColor:
                      index % 2 === 0
                        ? theme.palette.background.paper
                        : theme.palette.background.default,
                  })}
                >
                  <ListItemButton role={undefined} dense>
                    <ListItemText
                      primary={item.toUpperCase()}
                      sx={(theme) => ({
                        flex: 1,
                        color: theme.palette.text.primary, // adapts text color to light/dark mode
                      })}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </ViewportList>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '20px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleCopyToClipboard}
            sx={{
              borderRadius: '20px',
              fontWeight: '600',
              textTransform: 'none',
              padding: '6px 16px',
            }}
            data-testid="copy-to-clipboard-button"
          >
            {t('missingItemsDialogs.clipboard')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: '20px',
              fontWeight: '600',
              textTransform: 'none',
              padding: '6px 16px',
            }}
            data-testid="missing-items-close"
          >
            {t('missingItemsDialogs.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {snackBarOpen && (
        <Portal>
          <Snackbar
            open={snackBarOpen}
            autoHideDuration={3000}
            onClose={handleSnackBarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ zIndex: 9999, top: '113px!important' }}
            TransitionComponent={SlideLeftTransition}
            data-testid="snack-bar"
          >
            <Alert
              onClose={handleSnackBarClose}
              severity="success"
              sx={{ width: '100%' }}
            >
              {t('missingItemsDialogs.clipboard')}
            </Alert>
          </Snackbar>
        </Portal>
      )}
    </>
  );
}
